import { observer } from 'mobx-react';
import { Layout } from '@roc/feature-app-core';
import { DataGrid, AgGridColumnProps, SelectField } from '@roc/ui';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useStore } from '@roc/client-portal-shared/hooks';
import { Box, Typography } from '@material-ui/core';
import { BusinessUnit } from '@roc/feature-utils';
import { JourneyBuilderOverdueDashboard } from './journeyBuilderOverdueDashboard';

const columns: AgGridColumnProps[] = [
  {
    field: 'rmAe',
    headerName: 'RM/AE',
    minWidth: 160,
    maxWidth: 250,
    cellRenderer: 'overdueDrilldownRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalLeadsOnJourneys',
    headerName: 'Active Leads on Journeys',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'completedJourney',
    headerName: 'Leads Who Completed Journeys',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'leadsUnsubscribed',
    headerName: 'Leads Unsubscribed From Journeys',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'overdueSteps',
    headerName: 'Overdue Steps',
    minWidth: 160,
    maxWidth: 250,
    cellRenderer: 'overdueDrilldownRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalEmailClicks',
    headerName: 'Email Clicks',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalEmailOpens',
    headerName: 'Email Opens',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'crBgRunCadence',
    headerName: 'Credit/BG Check',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'loansSubmittedCadence',
    headerName: 'Total Loans',
    minWidth: 60,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
];

export const businessUnitOptions = [
  { label: 'All', value: null },
  { label: BusinessUnit.ROC, value: BusinessUnit.ROC },
  { label: BusinessUnit.CIVIC, value: BusinessUnit.CIVIC },
  { label: BusinessUnit.FACO, value: BusinessUnit.FACO },
]

export const JourneyBuilderDashboard = observer(() => {
  const { salesManagementStore } = useStore();
  const gridStore = salesManagementStore.journeyBuilderGridStore;
  const [businessUnit, setBusinessUnit] = useState<string>(salesManagementStore.businessUnit ?? null);
  const { search, pathname } = useLocation();
  const { push, replace } = useHistory();

  // the RM/AE drill-down lives in the url
  const urlParams = new URLSearchParams(search);
  const selectedUserId = Number(urlParams.get('userId'));
  const selectedRmAe = selectedUserId ? { userId: selectedUserId, rmAe: urlParams.get('rmAe') ?? '' } : null;

  const openOverdueSteps = (userId: number, rmAe: string) => {
    const params = new URLSearchParams(search);
    params.set('userId', String(userId));
    params.set('rmAe', rmAe);
    push({ pathname, search: params.toString() });
  };

  const closeOverdueSteps = () => {
    const params = new URLSearchParams(search);
    params.delete('userId');
    params.delete('rmAe');
    replace({ pathname, search: params.toString() });
  };

  useEffect(() => {
    gridStore.fetchGridData();
  }, [gridStore]);

  const onSortChanged = (params: any) => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onFilterChanged = (params: any) => {
    const filterModel = params.api.getFilterModel();
    gridStore.setFilterModel(filterModel);
  };

  const handleOnChange = (value) => {
    setBusinessUnit(value)
    salesManagementStore.setBusinessUnit(value)
    gridStore.fetchGridData();
  }

  const frameworkComponents = {
    overdueDrilldownRenderer: params => {
      const userId = params?.data?.userId;
      if (userId == null) return params.value ?? null;
      return (
        <a
          href="#"
          style={{ textDecoration: 'underline', color: '#5c94de', cursor: 'pointer' }}
          onClick={e => {
            e.preventDefault();
            openOverdueSteps(userId, params.data.rmAe);
          }}
        >
          {params.value}
        </a>
      );
    },
  };

  if (selectedRmAe) {
    return (
      <JourneyBuilderOverdueDashboard
        userId={selectedRmAe.userId}
        rmAe={selectedRmAe.rmAe}
        onBack={closeOverdueSteps}
      />
    );
  }

  return (
    <Layout>
      <Box display="flex" alignItems="center" style={{ gap: 12 }} p={1} pl={2} flexWrap="wrap">
        <Box>
          <SelectField
            value={businessUnit}
            options={businessUnitOptions}
            onChange={value => {
              handleOnChange(value)
            }}
            fullWidth
            variant={'outlined'}
            testId={'businessUnit'}
            style={{ height: '35px' }}
          />
        </Box>
      </Box>
      <DataGrid
        columns={columns}
        rows={gridStore.gridData.data?.rows ?? []}
        isLoading={gridStore.isLoading}
        showFilters={gridStore.showFilters}
        sortModel={gridStore.sortModel}
        filterModel={gridStore.filterModel}
        paginationData={gridStore.gridData.meta}
        setPageNumber={gridStore.setPageNumber}
        frameworkComponents={frameworkComponents}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
      />
    </Layout>
  );
});
